import type { SafeInfo } from "@safe-global/safe-apps-sdk";

import {
  isValidBuildReleaseId,
  isValidCommitSha,
  isValidIsoTimestamp,
  isValidSourceRepoUrl,
  normalizeHttpsOrigin,
  normalizeRuntimeOrigin,
  resolveRuntimeConfig,
  resolveRuntimeBuildMetadata,
  resolveSafeParentOriginAllowlist as resolveConfiguredSafeParentOriginAllowlist,
  toTrimmedString,
  type RuntimeConfigSnapshot
} from "@/config/runtimeConfig";
import {
  resolveRuntimeDowngrade,
  resolveRuntimeDowngradePolicy,
  resolveSafeConfigurationInventory,
  type RuntimeDowngradePolicy,
  type RuntimePostureDowngrade,
  type RuntimeSafeConfigurationInventory
} from "@/safe/runtimeSafePosture";

export { resolveRuntimeDowngradePolicy } from "@/safe/runtimeSafePosture";
export type {
  RuntimeDowngradePolicy,
  RuntimePostureDowngrade,
  RuntimePostureDowngradeCue,
  RuntimeSafeConfigurationInventory,
  RuntimeSafeInventoryStatus
} from "@/safe/runtimeSafePosture";

export type AppRunMode = "IN_SAFE" | "OUTSIDE_SAFE";
export type RuntimeMode =
  "INITIALIZING" | "OUTSIDE_SAFE_READ_ONLY" | "IN_SAFE_MAINNET" | "IN_SAFE_UNSUPPORTED_NETWORK";
export type RuntimeAuthenticityStatus = "VERIFIED" | "UNVERIFIED";

export type RuntimeProvenance = {
  currentOrigin: string;
  canonicalOrigin: string;
  sourceRepoUrl: string;
  appVersion: string;
  buildCommit: string;
  buildReleaseId: string;
  buildTimestamp: string;
};

export type RuntimeAuthenticityState = {
  status: RuntimeAuthenticityStatus;
  writeBlocked: boolean;
  reasons: string[];
  provenance: RuntimeProvenance;
};

export type RuntimePostureRiskCue =
  | "AUTHENTICITY_UNVERIFIED"
  | "SAFE_CONTEXT_UNAVAILABLE"
  | "SAFE_PARENT_ORIGIN_MISSING"
  | "SAFE_PARENT_ORIGIN_NOT_ALLOWLISTED"
  | "SAFE_CHAIN_UNSUPPORTED"
  | "SAFE_NONCE_MISSING"
  | "SAFE_NONCE_INVALID"
  | "SAFE_OWNERS_UNAVAILABLE"
  | "SAFE_THRESHOLD_UNAVAILABLE"
  | "SAFE_MODULES_UNAVAILABLE"
  | "SAFE_GUARD_UNAVAILABLE"
  | "SAFE_CONTRACT_VERSION_UNAVAILABLE"
  | "SAFE_FALLBACK_HANDLER_INVALID"
  | "MAINNET_RPC_MISSING"
  | "CANONICAL_ORIGIN_INVALID"
  | "SOURCE_REPO_INVALID"
  | "BUILD_COMMIT_INVALID"
  | "BUILD_RELEASE_INVALID"
  | "BUILD_TIMESTAMP_INVALID";

export type RuntimeSafeChainStatus = "MAINNET" | "UNSUPPORTED" | "UNAVAILABLE";
export type RuntimeSafeNonceStatus = "AVAILABLE" | "MISSING" | "INVALID" | "UNAVAILABLE";
export type RuntimeParentOriginStatus =
  "ALLOWLISTED" | "NOT_ALLOWLISTED" | "MISSING" | "UNAVAILABLE";

export type RuntimeSafeParentOrigin = {
  status: RuntimeParentOriginStatus;
  origin?: string;
  allowlist: readonly string[];
};

export type RuntimeConfigPosture = {
  mainnetRpcConfigured: boolean;
  canonicalOriginConfigured: boolean;
  sourceRepoConfigured: boolean;
  buildCommitConfigured: boolean;
  buildReleaseConfigured: boolean;
  buildTimestampConfigured: boolean;
};

export type RuntimePostureSnapshot = {
  safe: {
    detected: boolean;
    chainId?: number;
    chainStatus: RuntimeSafeChainStatus;
    nonce?: number;
    nonceStatus: RuntimeSafeNonceStatus;
    parentOrigin?: RuntimeSafeParentOrigin;
    inventory?: RuntimeSafeConfigurationInventory;
  };
  config: RuntimeConfigPosture;
  riskCues: RuntimePostureRiskCue[];
  downgrade?: RuntimePostureDowngrade;
};

export type RuntimeWritePathBlocker =
  | "OUTSIDE_SAFE"
  | "UNSUPPORTED_CHAIN"
  | "AUTHENTICITY_UNVERIFIED"
  | "SAFE_PARENT_ORIGIN_UNTRUSTED"
  | "SAFE_NONCE_MISSING"
  | "SAFE_NONCE_INVALID"
  | "SAFE_POSTURE_UNKNOWN";

export type RuntimeWritePathState = {
  failClosed: true;
  enabled: boolean;
  blockers: RuntimeWritePathBlocker[];
  invariants: {
    inSafeContext: boolean;
    mainnetChain: boolean;
    authenticityVerified: boolean;
    parentOriginAllowlisted?: boolean;
    deterministicSafeNonce: boolean;
    deterministicSafeConfig?: boolean;
  };
};

export type RuntimeCapabilities = {
  canRenderOperationalRoutes: boolean;
  canSubmitSafeProposals: boolean;
  writePathEnabled: boolean;
  writePathFailClosed: true;
};

export type RuntimeState = {
  mode: RuntimeMode;
  chainId?: number;
  unsupportedChainId?: number;
  capabilities: RuntimeCapabilities;
  authenticity: RuntimeAuthenticityState;
  posture: RuntimePostureSnapshot;
  writePath: RuntimeWritePathState;
};

export const MAINNET_CHAIN_ID = 1;

const MISSING_METADATA = "missing";

function toChainId(chainId: string | number | undefined): number | undefined {
  if (typeof chainId === "number" && Number.isFinite(chainId)) return chainId;
  if (typeof chainId === "string" && chainId.trim().length > 0) {
    const parsed = Number(chainId);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

export function normalizeOrigin(value: string | undefined): string | undefined {
  return normalizeRuntimeOrigin(value);
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function toSafeNonce(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isInteger(value) && value >= 0) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed >= 0) return parsed;
  }
  return undefined;
}

export function resolveSafeParentOriginAllowlist(rawValue?: string): string[] {
  return resolveConfiguredSafeParentOriginAllowlist(rawValue);
}

export function toAllowedDomainMatchers(origins: readonly string[]): RegExp[] {
  return origins.map((origin) => new RegExp(`^${escapeRegex(origin)}$`));
}

function isAllowlistedOrigin(origin: string | undefined, allowlist: readonly string[]): boolean {
  if (!origin) return false;
  return allowlist.includes(origin);
}

export function evaluateSafeParentOrigin(params: {
  embedded: boolean;
  parentOrigin: string | undefined;
  allowlist: readonly string[];
}): RuntimeSafeParentOrigin {
  const { embedded, parentOrigin, allowlist } = params;
  const normalizedOrigin = normalizeOrigin(parentOrigin);

  if (!embedded) {
    return {
      status: "UNAVAILABLE",
      allowlist
    };
  }

  if (!normalizedOrigin) {
    return {
      status: "MISSING",
      allowlist
    };
  }

  if (!isAllowlistedOrigin(normalizedOrigin, allowlist)) {
    return {
      status: "NOT_ALLOWLISTED",
      origin: normalizedOrigin,
      allowlist
    };
  }

  return {
    status: "ALLOWLISTED",
    origin: normalizedOrigin,
    allowlist
  };
}

export function getDocumentReferrerOrigin(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return normalizeOrigin(document.referrer);
}

export function isEmbeddedRuntime(): boolean {
  return typeof window !== "undefined" && window.parent !== window;
}

export type RuntimeAuthenticityInput = {
  currentOrigin?: string | undefined;
  canonicalOrigin?: string | undefined;
  sourceRepoUrl?: string | undefined;
  appVersion?: string | undefined;
  buildCommit?: string | undefined;
  buildReleaseId?: string | undefined;
  buildTimestamp?: string | undefined;
};

export function evaluateRuntimeAuthenticity(
  input: RuntimeAuthenticityInput
): RuntimeAuthenticityState {
  const rawCurrentOrigin = toTrimmedString(input.currentOrigin);
  const rawCanonicalOrigin = toTrimmedString(input.canonicalOrigin);
  const sourceRepoUrl = toTrimmedString(input.sourceRepoUrl);
  const appVersion = toTrimmedString(input.appVersion);
  const buildCommit = toTrimmedString(input.buildCommit);
  const buildReleaseId = toTrimmedString(input.buildReleaseId);
  const buildTimestamp = toTrimmedString(input.buildTimestamp);

  const currentOrigin = normalizeOrigin(rawCurrentOrigin);
  const canonicalOrigin = normalizeHttpsOrigin(rawCanonicalOrigin);

  const reasons: string[] = [];

  if (!canonicalOrigin) {
    reasons.push("Canonical origin metadata is missing or invalid.");
  }

  if (!currentOrigin) {
    reasons.push("Runtime origin metadata is missing or invalid.");
  }

  if (canonicalOrigin && currentOrigin && currentOrigin !== canonicalOrigin) {
    reasons.push(
      `Runtime origin ${currentOrigin} does not match canonical origin ${canonicalOrigin}.`
    );
  }

  if (!isValidCommitSha(buildCommit)) {
    reasons.push("Build commit metadata is missing or invalid.");
  }

  if (!isValidBuildReleaseId(buildReleaseId)) {
    reasons.push("Build release ID metadata is missing or invalid.");
  }

  if (!isValidIsoTimestamp(buildTimestamp)) {
    reasons.push("Build timestamp metadata is missing or invalid.");
  }

  if (!isValidSourceRepoUrl(sourceRepoUrl)) {
    reasons.push("Source repository metadata is missing or invalid.");
  }

  return {
    status: reasons.length > 0 ? "UNVERIFIED" : "VERIFIED",
    writeBlocked: reasons.length > 0,
    reasons,
    provenance: {
      currentOrigin: rawCurrentOrigin ?? MISSING_METADATA,
      canonicalOrigin: rawCanonicalOrigin ?? MISSING_METADATA,
      sourceRepoUrl: sourceRepoUrl ?? MISSING_METADATA,
      appVersion: appVersion ?? MISSING_METADATA,
      buildCommit: buildCommit ?? MISSING_METADATA,
      buildReleaseId: buildReleaseId ?? MISSING_METADATA,
      buildTimestamp: buildTimestamp ?? MISSING_METADATA
    }
  };
}

export function resolveRuntimeAuthenticity(
  config?: Pick<RuntimeConfigSnapshot, "build">
): RuntimeAuthenticityState {
  const currentOrigin = typeof window === "undefined" ? undefined : window.location.origin;
  const input: RuntimeAuthenticityInput = {
    ...(config?.build ?? resolveRuntimeBuildMetadata()),
    ...(currentOrigin ? { currentOrigin } : {})
  };
  return evaluateRuntimeAuthenticity(input);
}

export function resolveRuntimeConfigPosture(
  config: Pick<RuntimeConfigSnapshot, "endpoints" | "build"> = resolveRuntimeConfig()
): RuntimeConfigPosture {
  const mainnetRpcUrl = config.endpoints.mainnetRpcUrl;
  const metadata = config.build;
  const canonicalOrigin = toTrimmedString(metadata.canonicalOrigin);
  const sourceRepoUrl = toTrimmedString(metadata.sourceRepoUrl);
  const buildCommit = toTrimmedString(metadata.buildCommit);
  const buildReleaseId = toTrimmedString(metadata.buildReleaseId);
  const buildTimestamp = toTrimmedString(metadata.buildTimestamp);

  return {
    mainnetRpcConfigured: Boolean(mainnetRpcUrl),
    canonicalOriginConfigured: Boolean(normalizeHttpsOrigin(canonicalOrigin)),
    sourceRepoConfigured: isValidSourceRepoUrl(sourceRepoUrl),
    buildCommitConfigured: isValidCommitSha(buildCommit),
    buildReleaseConfigured: isValidBuildReleaseId(buildReleaseId),
    buildTimestampConfigured: isValidIsoTimestamp(buildTimestamp)
  };
}

function resolveRuntimePostureSnapshot(params: {
  mode: AppRunMode;
  safe: SafeInfo | undefined;
  authenticity: RuntimeAuthenticityState;
  config: RuntimeConfigPosture;
  parentOrigin: RuntimeSafeParentOrigin;
  downgradePolicy: RuntimeDowngradePolicy;
}): RuntimePostureSnapshot {
  const { mode, safe, authenticity, config, parentOrigin, downgradePolicy } = params;

  const safeDetected = mode === "IN_SAFE" && Boolean(safe);
  const chainId = safeDetected ? toChainId(safe?.chainId) : undefined;
  const chainStatus: RuntimeSafeChainStatus = !safeDetected
    ? "UNAVAILABLE"
    : chainId === MAINNET_CHAIN_ID
      ? "MAINNET"
      : "UNSUPPORTED";

  const nonceCandidate = safeDetected
    ? (safe as { nonce?: unknown } | undefined)?.nonce
    : undefined;
  const nonceStatus: RuntimeSafeNonceStatus = !safeDetected
    ? "UNAVAILABLE"
    : nonceCandidate === undefined || nonceCandidate === null || nonceCandidate === ""
      ? "MISSING"
      : toSafeNonce(nonceCandidate) === undefined
        ? "INVALID"
        : "AVAILABLE";
  const nonce = nonceStatus === "AVAILABLE" ? toSafeNonce(nonceCandidate) : undefined;
  const inventory = resolveSafeConfigurationInventory({ safeDetected, safe });
  const downgrade = resolveRuntimeDowngrade({
    safeDetected,
    inventory,
    policy: downgradePolicy
  });

  const riskCues: RuntimePostureRiskCue[] = [];

  if (authenticity.writeBlocked) riskCues.push("AUTHENTICITY_UNVERIFIED");
  if (!safeDetected) riskCues.push("SAFE_CONTEXT_UNAVAILABLE");
  if (parentOrigin.status === "MISSING") riskCues.push("SAFE_PARENT_ORIGIN_MISSING");
  if (parentOrigin.status === "NOT_ALLOWLISTED")
    riskCues.push("SAFE_PARENT_ORIGIN_NOT_ALLOWLISTED");
  if (chainStatus === "UNSUPPORTED") riskCues.push("SAFE_CHAIN_UNSUPPORTED");
  if (nonceStatus === "MISSING") riskCues.push("SAFE_NONCE_MISSING");
  if (nonceStatus === "INVALID") riskCues.push("SAFE_NONCE_INVALID");
  if (safeDetected && inventory.ownersStatus !== "AVAILABLE")
    riskCues.push("SAFE_OWNERS_UNAVAILABLE");
  if (safeDetected && inventory.thresholdStatus !== "AVAILABLE")
    riskCues.push("SAFE_THRESHOLD_UNAVAILABLE");
  if (safeDetected && inventory.modulesStatus !== "AVAILABLE")
    riskCues.push("SAFE_MODULES_UNAVAILABLE");
  if (safeDetected && inventory.guardStatus !== "AVAILABLE")
    riskCues.push("SAFE_GUARD_UNAVAILABLE");
  if (safeDetected && inventory.contractVersionStatus !== "AVAILABLE")
    riskCues.push("SAFE_CONTRACT_VERSION_UNAVAILABLE");
  if (safeDetected && inventory.fallbackHandlerStatus === "INVALID")
    riskCues.push("SAFE_FALLBACK_HANDLER_INVALID");
  if (!config.mainnetRpcConfigured) riskCues.push("MAINNET_RPC_MISSING");
  if (!config.canonicalOriginConfigured) riskCues.push("CANONICAL_ORIGIN_INVALID");
  if (!config.sourceRepoConfigured) riskCues.push("SOURCE_REPO_INVALID");
  if (!config.buildCommitConfigured) riskCues.push("BUILD_COMMIT_INVALID");
  if (!config.buildReleaseConfigured) riskCues.push("BUILD_RELEASE_INVALID");
  if (!config.buildTimestampConfigured) riskCues.push("BUILD_TIMESTAMP_INVALID");

  const safePosture: RuntimePostureSnapshot["safe"] = {
    detected: safeDetected,
    chainStatus,
    nonceStatus,
    parentOrigin,
    inventory
  };
  if (chainId !== undefined) safePosture.chainId = chainId;
  if (nonce !== undefined) safePosture.nonce = nonce;

  return {
    safe: safePosture,
    config,
    riskCues,
    downgrade
  };
}

function resolveRuntimeWritePath(params: {
  mode: AppRunMode;
  authenticity: RuntimeAuthenticityState;
  posture: RuntimePostureSnapshot;
}): RuntimeWritePathState {
  const { mode, authenticity, posture } = params;
  const inSafeContext = mode === "IN_SAFE";
  const mainnetChain = posture.safe.chainStatus === "MAINNET";
  const authenticityVerified = !authenticity.writeBlocked;
  const parentOriginAllowlisted = posture.safe.parentOrigin?.status === "ALLOWLISTED";
  const deterministicSafeNonce = posture.safe.nonceStatus === "AVAILABLE";
  const safeInventory = posture.safe.inventory;
  const deterministicSafeConfig =
    safeInventory?.ownersStatus === "AVAILABLE" &&
    safeInventory.thresholdStatus === "AVAILABLE" &&
    safeInventory.modulesStatus === "AVAILABLE" &&
    safeInventory.guardStatus === "AVAILABLE" &&
    safeInventory.contractVersionStatus === "AVAILABLE";

  const blockers: RuntimeWritePathBlocker[] = [];
  if (!inSafeContext) blockers.push("OUTSIDE_SAFE");
  if (inSafeContext && !mainnetChain) blockers.push("UNSUPPORTED_CHAIN");
  if (!authenticityVerified) blockers.push("AUTHENTICITY_UNVERIFIED");
  if (inSafeContext && !parentOriginAllowlisted) blockers.push("SAFE_PARENT_ORIGIN_UNTRUSTED");
  if (inSafeContext && !deterministicSafeNonce) {
    blockers.push(
      posture.safe.nonceStatus === "INVALID" ? "SAFE_NONCE_INVALID" : "SAFE_NONCE_MISSING"
    );
  }
  if (inSafeContext && !deterministicSafeConfig) blockers.push("SAFE_POSTURE_UNKNOWN");

  return {
    failClosed: true,
    enabled: blockers.length === 0,
    blockers,
    invariants: {
      inSafeContext,
      mainnetChain,
      authenticityVerified,
      parentOriginAllowlisted: inSafeContext ? parentOriginAllowlisted : false,
      deterministicSafeNonce: inSafeContext ? deterministicSafeNonce : false,
      deterministicSafeConfig: inSafeContext ? deterministicSafeConfig : false
    }
  };
}

function runtimeCapabilitiesFor(params: {
  mode: RuntimeMode;
  writePathEnabled: boolean;
}): RuntimeCapabilities {
  const { mode, writePathEnabled } = params;
  if (mode === "OUTSIDE_SAFE_READ_ONLY") {
    return {
      canRenderOperationalRoutes: true,
      canSubmitSafeProposals: false,
      writePathEnabled: false,
      writePathFailClosed: true
    };
  }

  if (mode === "IN_SAFE_MAINNET") {
    return {
      canRenderOperationalRoutes: true,
      canSubmitSafeProposals: writePathEnabled,
      writePathEnabled,
      writePathFailClosed: true
    };
  }

  return {
    canRenderOperationalRoutes: false,
    canSubmitSafeProposals: false,
    writePathEnabled: false,
    writePathFailClosed: true
  };
}

export type ResolveRuntimeStateParams = {
  mode: AppRunMode;
  safe?: SafeInfo;
  initializing?: boolean;
  authenticity: RuntimeAuthenticityState;
  config: RuntimeConfigPosture;
  parentOrigin?: RuntimeSafeParentOrigin;
  downgradePolicy?: RuntimeDowngradePolicy;
};

export function resolveRuntimeState(params: ResolveRuntimeStateParams): RuntimeState {
  const {
    mode,
    safe,
    initializing = false,
    authenticity,
    config,
    parentOrigin = { status: "UNAVAILABLE", allowlist: [] },
    downgradePolicy = resolveRuntimeDowngradePolicy()
  } = params;
  const posture = resolveRuntimePostureSnapshot({
    mode,
    safe,
    authenticity,
    config,
    parentOrigin,
    downgradePolicy
  });
  const writePath = resolveRuntimeWritePath({ mode, authenticity, posture });

  if (initializing) {
    return {
      mode: "INITIALIZING",
      capabilities: runtimeCapabilitiesFor({ mode: "INITIALIZING", writePathEnabled: false }),
      authenticity,
      posture,
      writePath
    };
  }

  if (mode === "OUTSIDE_SAFE") {
    const outsideSafeBlockedByOrigin =
      parentOrigin.status === "MISSING" || parentOrigin.status === "NOT_ALLOWLISTED";
    return {
      mode: "OUTSIDE_SAFE_READ_ONLY",
      capabilities: outsideSafeBlockedByOrigin
        ? {
            canRenderOperationalRoutes: false,
            canSubmitSafeProposals: false,
            writePathEnabled: false,
            writePathFailClosed: true
          }
        : runtimeCapabilitiesFor({
            mode: "OUTSIDE_SAFE_READ_ONLY",
            writePathEnabled: false
          }),
      authenticity,
      posture,
      writePath
    };
  }

  const chainId = posture.safe.chainId;
  if (posture.safe.chainStatus === "MAINNET") {
    return {
      mode: "IN_SAFE_MAINNET",
      chainId: chainId ?? MAINNET_CHAIN_ID,
      capabilities: runtimeCapabilitiesFor({
        mode: "IN_SAFE_MAINNET",
        writePathEnabled: writePath.enabled
      }),
      authenticity,
      posture,
      writePath
    };
  }

  return {
    mode: "IN_SAFE_UNSUPPORTED_NETWORK",
    ...(chainId !== undefined ? { chainId, unsupportedChainId: chainId } : {}),
    capabilities: runtimeCapabilitiesFor({
      mode: "IN_SAFE_UNSUPPORTED_NETWORK",
      writePathEnabled: false
    }),
    authenticity,
    posture,
    writePath
  };
}
